import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Information } from "../../domain/information/Information";
import { Paths } from "../../Paths";
import { useInformationList } from "../hooks/information/useInformationList";
import { useNavigate } from "react-router-dom";

type ContextType = {
    pageCount: number
    currentPage: number
    handleInformationPager: (page: number) => void
    displayInformationList: Information[]
};

const countPerPage = 5;

const defaultContextValue: ContextType = {
    pageCount: 1,
    currentPage: 0,
    handleInformationPager: (_page: number) => {},
    displayInformationList: []
};

type PathParams = {
    page?: string
}

export const InformationListContext = createContext(defaultContextValue);

export const InformationListProvider = React.memo(function InformationListProvider(props: Props) {
    const { page: pageOfPath }  = useParams<PathParams>();

    const navigate = useNavigate();

    const informationList = useInformationList();
 
    const pageCount = informationList ? Math.ceil(informationList.length / countPerPage) : 1;

    const [currentPage, setCurrentPage] = useState(0);

    const handleInformationPager = useCallback((page: number) => navigate(`${Paths.information}${Paths.list}/${page + 1}`), [navigate]);

    const displayInformationList = useMemo(() => {
        const start = countPerPage * currentPage;
        const end = start + countPerPage;
        return informationList ? informationList.slice(start, end) : [];
    }, [currentPage, informationList]);

    const value = useMemo(() => ({
        pageCount: pageCount,
        currentPage: currentPage,
        handleInformationPager: handleInformationPager,
        displayInformationList: displayInformationList
    }), [pageCount, currentPage, handleInformationPager, displayInformationList]);

    useEffect(() => {
        if (!pageOfPath) {
            setCurrentPage(0);
        } else {
            setCurrentPage(Number(pageOfPath) - 1);
        }
    }, [pageOfPath]);

    return (
        <InformationListContext.Provider value={value}>{props.children}</InformationListContext.Provider>
    );
});

type Props = {
    children: ReactNode
};
